import {
  AttachMoney,
  CalendarToday,
  Description,
  Percent,
  Timeline as TimelineIcon,
} from '@mui/icons-material';
import { Box, Grid, Typography } from '@mui/material';
import { formatDate, formatMoney } from 'lib/functions/utils';
import React from 'react';

import RateHistoryTimeline from '../RateHistoryTimeline';
import { getConciliationTypeLabel } from '../rateHistoryUtils';
import { DocumentConciliationRateHistory } from '../types';

interface SummaryTabProps {
  history: DocumentConciliationRateHistory;
}

const SummaryTab: React.FC<SummaryTabProps> = ({ history }) => {
  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={5}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box
            sx={{
              backgroundColor: 'rgba(156, 39, 176, 0.08)',
              borderRadius: '8px',
              px: 1.5,
              py: 0.8,
              border: '1px solid rgba(156, 39, 176, 0.15)',
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: 'purple',
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                textAlign: 'center',
              }}
            >
              Resumen de Tasas
            </Typography>
          </Box>

          <Box
            sx={{
              backgroundColor: 'rgba(0, 0, 0, 0.02)',
              borderRadius: '8px',
              p: 2,
              border: '1px solid rgba(0, 0, 0, 0.08)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 1.5,
              }}
            >
              <Description sx={{ fontSize: '1rem', color: 'purple' }} />
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  fontSize: '0.875rem',
                }}
              >
                Información General
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarToday
                  sx={{
                    fontSize: '0.875rem',
                    color: 'text.secondary',
                  }}
                />
                <Typography variant="body2">
                  <strong>Fecha de pago:</strong>{' '}
                  {formatDate(new Date(history.paymentDate), 'dd-mm-yyyy')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Percent
                  sx={{
                    fontSize: '0.875rem',
                    color: 'text.secondary',
                  }}
                />
                <Typography variant="body2">
                  <strong>Tasa actual:</strong> {history.currentArrearRate}%
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Description
                  sx={{
                    fontSize: '0.875rem',
                    color: 'text.secondary',
                  }}
                />
                <Typography variant="body2">
                  <strong>Tipo de cálculo:</strong>{' '}
                  {history.arrearType === 'table'
                    ? 'Tabla segmentada'
                    : 'Lineal'}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              backgroundColor: 'rgba(0, 0, 0, 0.02)',
              borderRadius: '8px',
              p: 2,
              border: '1px solid rgba(0, 0, 0, 0.08)',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 1.5,
              }}
            >
              <AttachMoney sx={{ fontSize: '1rem', color: 'purple' }} />
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  fontSize: '0.875rem',
                }}
              >
                Resumen de Conciliaciones
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
              }}
            >
              <Typography variant="body2">
                <strong>Total conciliaciones:</strong>{' '}
                {history.summary.totalConciliations}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AttachMoney
                  sx={{
                    fontSize: '0.875rem',
                    color: 'text.secondary',
                  }}
                />
                <Typography variant="body2">
                  <strong>Monto total:</strong>{' '}
                  {formatMoney(history.summary.totalAmount)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Percent
                  sx={{
                    fontSize: '0.875rem',
                    color: 'text.secondary',
                  }}
                />
                <Typography variant="body2">
                  <strong>Tasa promedio:</strong>{' '}
                  {history.summary.averageRate.toFixed(2)}%
                </Typography>
              </Box>
              {history.summary.dateRange.firstConciliation && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarToday
                    sx={{
                      fontSize: '0.875rem',
                      color: 'text.secondary',
                    }}
                  />
                  <Typography variant="body2">
                    <strong>Primera conciliación:</strong>{' '}
                    {formatDate(
                      new Date(history.summary.dateRange.firstConciliation),
                      'dd-mm-yyyy',
                    )}
                  </Typography>
                </Box>
              )}
              {history.summary.dateRange.lastConciliation && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarToday
                    sx={{
                      fontSize: '0.875rem',
                      color: 'text.secondary',
                    }}
                  />
                  <Typography variant="body2">
                    <strong>Última conciliación:</strong>{' '}
                    {formatDate(
                      new Date(history.summary.dateRange.lastConciliation),
                      'dd-mm-yyyy',
                    )}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {Object.keys(history.summary.rateByType).length > 0 && (
            <Box
              sx={{
                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                borderRadius: '8px',
                p: 2,
                border: '1px solid rgba(0, 0, 0, 0.08)',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 1.5,
                }}
              >
                <Percent sx={{ fontSize: '1rem', color: 'purple' }} />
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    fontSize: '0.875rem',
                  }}
                >
                  Tasas por Tipo
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                }}
              >
                {Object.entries(history.summary.rateByType).map(
                  ([type, rate]) => (
                    <Box
                      key={type}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Percent
                        sx={{
                          fontSize: '0.875rem',
                          color: 'text.secondary',
                        }}
                      />
                      <Typography variant="body2">
                        <strong>{getConciliationTypeLabel(type)}:</strong>{' '}
                        {rate}%
                      </Typography>
                    </Box>
                  ),
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Grid>
      <Grid item xs={12} md={7}>
        <Box
          sx={{
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
            borderRadius: '8px',
            p: 2,
            border: '1px solid rgba(0, 0, 0, 0.08)',
            height: 'fit-content',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 2,
            }}
          >
            <TimelineIcon sx={{ fontSize: '1rem', color: 'purple' }} />
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                fontSize: '0.875rem',
              }}
            >
              Timeline
            </Typography>
          </Box>
          <RateHistoryTimeline timeline={history.timeline} />
        </Box>
      </Grid>
    </Grid>
  );
};

export default SummaryTab;
